import { Injectable, OnDestroy } from '@angular/core';

import { catchError, filter, map, mergeMap, take } from 'rxjs/operators';
import {
    BehaviorSubject,
    combineLatest,
    EMPTY,
    forkJoin,
    Observable,
    Observer,
    of,
    Subject,
    Subscription,
    switchMap,
    tap
} from 'rxjs';
import { ToastMessageOptions, MessageService } from 'primeng/api';
import { isString } from 'underscore';

import { userPermissionsEnum } from '../../../Core/Users/enums/user-permissions.enum';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';

import { SavedMentionSearchesApi } from '../../../api/mentions/saved-mention-searches.api';
import { ReportsApi } from '../../../api/reports/reports.api';
import { SearchCriteriaQuerySentimentsEnum } from '../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { ReportApiModel } from '../../../api/reports/model/report-api.model';
import { ReportSeriesApiModel } from '../../../api/reports/model/report-series-api.model';
import { MentionsApi } from '../../../api/mentions/mentions.api';


import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { userAppPreferencesDefault } from '../../../Core/Users/data/user-app-preferences-default';

import { MentionSearchModel } from '../../../Monitor/Shared/models/mention-search.model';
import { MentionSearchQueryModel } from '../../../Monitor/Shared/models/mention-search-query.model';
import { TopicsService } from '../../../Monitor/Core/services/topics.service';
import { FolderService } from '../../../Monitor/Core/services/folder.service';
import { MediaSubscriptionService } from '../../../Monitor/Core/services/media-subscription.service';

import { TagsService } from '../../../Common/Tags/services/tags.service';
import { TagGroupsService } from '../../../Common/Tags/services/tag-groups.service';
import { SourceGroupService } from '../../../Common/Sources/services/source-group.service';

import { UserPreferencesService } from '../../../Account/UserPreferences/services/user-preferences.service';

import { messageSeverityEnum } from '../../Misc/enums/message-severity.enum';
import { AuditableModel } from '../../Misc/models/auditable.model';
import { ConfirmDialogModel } from '../../Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { LeftSidebarListItemButton } from '../../Layout/models/left-sidebar-list-item-button.model';
import { WebsocketAwareNeptuneService, ResourceType } from '../../../Bootstrap/services/websocket-aware-neptune.service';
import { SavedSearchType } from '../../MonitorShared/enum/saved-search-type.enum';
import { FilterListProviderServiceInterface } from '../../Filter/services/filter-list-provider-service.interface';
import { JournodexRegionsService } from '../../Journodex/services/journodex-regions.service';

import { ReportsRouteModel } from '../../../Reports/ReportsRouter/models/reports-route.model';
import { ReportsRoutingService } from '../../../Reports/Core/services/reports-routing.service';

import { BlockModel } from '../../PageBuilder/models/block.model';
import { BlockTypeModel } from '../../PageBuilder/models/block-type.model';

import { ReportModel } from '../models/report.model';
import { ReportListItemModel } from '../models/report-list-item.model';
import { ChartDisplayElementModel } from '../models/chart-display-element.model';
import { ReportSeriesModel } from '../models/report-series.model';
import { reportBlockTypesMap } from '../models/reports-block-types.model';
import { SeriesPickerFieldModel } from '../models/series-picker-field.model';
import { SeriesPickerCompareFieldModel } from '../models/series-picker-compare-field.model';

import { ReportBlockTypeEnum, reportBlockTypeEnumToDefaultChartSpecificType, reportChartInfoMap } from '../enums/report-block-type.enum';
import { SeriesSourceTypesEnum, SeriesTypeEnum } from '../enums/series-type.enum';
import { ChartMetricsEnum } from '../enums/chart-metrics.enum';
import { ReportsRouteTypeEnum } from '../enums/reports-route-type.enum';
import { currencySymbolMap, UserCurrency } from '../../../Core/Users/enums/user-currency';

import { TextInputDialogModel } from '../../Dialogs/models/text-input-dialog.model';
import {
    TextInputDialogComponent
} from '../../Dialogs/components/TextInputDialog/text-input-dialog.component';
import { ActionsEnum } from '../../Basic/enums/actions.enum';
import { ThemeService } from '../../../Social/Intelligence/services/theme.service';
import { MapChartChoroplethInterpolationTypeEnum, MapChartColorSchemeEnum } from '../../Charts/models/map-chart.model';
import { NewResourceDialogComponent } from '../../Dialogs/components/NewResourceDialog/new-resource-dialog.component';
import { NewResourceDialogModel } from '../../Dialogs/models/new-resource-dialog.model';
import { DisplayGroupModel } from '../../Layout/models/display-group.model';
import { ChartTimeIntervalsEnum } from '../enums/chart-time-intervals.enum';
import { Mention } from '../../Mentions/models/mention';
import { UserAppPreferenceChartColors } from '../../../Core/Users/models/user-app-preferences-chart-colors.model';
import { DisplayGroupsService } from '../../Layout/services/display-groups.service';
import { NeoFacetRequestApiModel } from '@api/mentions/model/neo-facet-request-api.model';

@Injectable()
export class ReportsService implements OnDestroy, FilterListProviderServiceInterface {
    public static readonly DUPLICATE_ERROR_STRING = 'report name already exists';

    private subscriptions: Subscription[];

    private currentReport: ReportModel;
    private cachedCurrentReport: ReportModel;
    private currentReportProvider: BehaviorSubject<ReportModel>;
    private showQuickReportDialogProvider: Subject<boolean>;

    private currentRoute: ReportsRouteModel;

    private reportListItems: ReportListItemModel[];
    private reportListItemsProvider: BehaviorSubject<ReportListItemModel[]>;

    private currentRequest: Subscription;

    private isSaving: boolean = false;
    private canLeaveBypass: boolean = false;

    private defaultBarColor: string;
    private defaultLineColor: string;
    private defaultChartColors: UserAppPreferenceChartColors;

    private currency: UserCurrency;

    /**
     * Class Constructor
     */
    constructor(
        private reportsApi: ReportsApi,
        private savedSearchesApi: SavedMentionSearchesApi,
        private routingService: ReportsRoutingService,
        private localizationService: LocalizationService,
        private messageService: MessageService,
        private journodexService: JournodexRegionsService,
        private coreDialogService: CoreDialogService,
        private websocketEvents: WebsocketAwareNeptuneService,
        private topicsService: TopicsService,
        private folderService: FolderService,
        private tagsService: TagsService,
        private tagGroupsService: TagGroupsService,
        private mediaSubscriptionService: MediaSubscriptionService,
        private sourceGroupService: SourceGroupService,
        private currentUserStateService: CurrentUserStateService,
        private userPreferencesService: UserPreferencesService,
        private themeService: ThemeService,
        public mentionsApi: MentionsApi,
        private displayGroupsService: DisplayGroupsService
    ) {
        this.currentReportProvider = new BehaviorSubject<ReportModel>(null);

        this.reportListItemsProvider = new BehaviorSubject<ReportListItemModel[]>(null);

        this.showQuickReportDialogProvider = new Subject<boolean>();

        this.setSubscriptions();
    }

    /**
     * Sets subscriptions, pushing to array for easy cleanup later
     */
    private setSubscriptions(): void {
        this.subscriptions = [];
        this.subscriptions.push(
            this.routingService.getCurrentRoute$().subscribe(routeModel => {
                this.currentRoute = routeModel;
                this.updateReportFromRoute(routeModel);
            }),
            this.websocketEvents.getWebsocketMessages().pipe(
                filter(x => !!x)
            ).subscribe(message => {
                if (message.resourceType === ResourceType.reports) {
                    this.updateReportListItems();
                }
            }),
            this.userPreferencesService.getUserAppPreferences().pipe(
                map(userAppPrefs => userAppPrefs.reportsPrefs.defaultChartColors),
                catchError(() => of(userAppPreferencesDefault.reportsPrefs.defaultChartColors))
            ).subscribe(colors => {
                this.defaultChartColors = colors;
                this.defaultBarColor = colors?.['barColors']?.['barColor'];
                this.defaultLineColor = colors?.['generalColors']?.['color1'];
            }),
            this.currentUserStateService.getUserCurrency().subscribe( currency => this.currency = currency)

        );
    }

    /**
     * Gets the provider for the current report
     */
    public getCurrentReport(): Observable<ReportModel> {
        return this.currentReportProvider;
    }

    /**
     * The cached current report
     */
    public getCachedCurrentReport(): ReportModel {
        return this.cachedCurrentReport;
    }

    /**
     * Updates the currentReport
     */
    public setCurrentReport(report: ReportModel): void {
        this.currentReport = report;
        this.currentReportProvider.next(this.currentReport);
    }

    /**
     * Shortcut to just update the blocks of the current report
     */
    public setCurrentReportBlocks(blocks: BlockModel[]): void {
        if (!this.currentReport) {
            return;
        }

        this.currentReport.blocks = blocks;
        this.updateCurrentReportBlocks();
        this.currentReportProvider.next(this.currentReport);
    }

    /**
     * Starts the delete process, deleting if user feedback confirms
     */
    public confirmDeleteReport(reportToDelete: ReportListItemModel): void {
        const io: ConfirmDialogModel = {
            inputs: {
                heading: 'Reports.deleteReport',
                message: this.localizationService.transform('Reports.confirmDeleteReportMessage', { name: reportToDelete.name }),
                alertMessage: this.localizationService.transform('Monitoring.confirmDeleteReport'),
                alertType: messageSeverityEnum.warning,
                okLabel: 'Core.button.delete',
                id: 'ReportsDelete'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.deleteReport(reportToDelete);
                    } else {
                        this.updateReportListItems();
                    }
                }
            }
        };
        if (this.currentReport?.id === reportToDelete.id) {
            // deleting the current report
            this.showCanLeaveDialog().subscribe( respond => {
                if (respond){
                    this.refreshCurrentReport();
                    this.coreDialogService.open(ConfirmDialogComponent, io, undefined, 'ConfirmDelete');
                }
            });
        } else {
            this.coreDialogService.open(ConfirmDialogComponent, io);
        }
    }

    /**
     * Similar logic is used across different services.
     *
     * @TODO Consider consolidating repeated bulk deletion logic using shared CRUD interfaces.
     */
    public confirmBulkDeleteReport(items: AuditableModel[], groups: DisplayGroupModel[]): Observable<boolean> {
        const list = items.map(item => item.name);
        const secondaryList = groups.map(item => item.name);

        return new Observable<boolean>((subscriber) => {
            let isListSelected: boolean = true;
            let isSecondaryListSelected: boolean = false;

            const io: ConfirmDialogModel = {
                inputs: {
                    id: 'ReportsBulkDelete',
                    heading: 'Monitoring.bulkDeleteReport',
                    alertType: messageSeverityEnum.warning,
                    alertMessage: 'Monitoring.confirmBulkDeleteReport.alertMessage',
                    message: 'Common.confirmDialog.message',
                    list,
                    listLabel: 'Monitoring.confirmBulkDeleteReport.checkboxLabel',
                    isListSelected,
                    secondaryList,
                    secondaryListLabel: 'Common.confirmDialog.checkboxLabel.groups',
                    isSecondaryListSelected,
                    okLabel: 'Core.button.delete'
                },
                outputs: {
                    listSelectionChanged: (isSelected: boolean) => {
                        isListSelected = isSelected;
                    },
                    secondaryListSelectionChanged: (isSelected: boolean) => {
                        isSecondaryListSelected = isSelected;
                    },
                    dialogClose: (confirmed: any) => {
                        if (confirmed) {
                            if (isListSelected) {
                                const ids = items.map(item => item.id);
                                this.bulkDeleteReports(ids);
                            }

                            if (isSecondaryListSelected) {
                                groups.forEach(group => this.displayGroupsService.deleteGroup(group));
                            }
                        }
                        subscriber.next(confirmed);
                        subscriber.complete();
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, io);
        });

    }

    private bulkDeleteReports(ids: number[]) {
        const observables = ids.map(id => {
            return this.reportsApi.delete(id);
        });
        forkJoin(observables).subscribe({
            next: (result) => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Reports.bulkDeleteReportSuccessHeading',
                    detail: this.localizationService.transform('Reports.bulkDeleteReportSuccessMessage', {number: result.length})
                });
                if (this.currentReport && ids.includes(this.currentReport.id)) {
                    this.routingService.gotoReports();
                }
                this.updateReportListItems();
            },
            error: (err) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Reports.bulkDeleteReportErrorHeading',
                    detail: 'Reports.bulkDeleteReportErrorMessage'
                });
                this.updateReportListItems();
            }
        });
    }

    /**
     * Update from LHP with text input dialog
     * @param report
     */
    public renameReport(report: ReportListItemModel){
        let currentReport: ReportModel ;
        const inputConfig: TextInputDialogModel = {
            inputs: {
                width: 280,
                heading: 'Reports.renameReport',
                okLabel: 'Lhp.actions.rename',
                description: 'Reports.copy.name',
                placeholder: 'Reports.copy.placeholder',
                initialValue: report.name,
                formValidator: (name: string) => {
                    const duplicates = this.reportListItems.filter(element => element.name.toLowerCase() === name.toLowerCase());
                    if (duplicates.length > 0 && duplicates[0].id !== report.id){
                        return ['Reports.renameReport.unique'];
                    }else{
                        return [];
                    }
                },
                submit: (name: string) => {
                    this.getReport(report.id).pipe(
                        mergeMap(_report => {
                            _report.name = name;
                            currentReport = _report;
                            return this.reportsApi.update(_report.toApiModel());
                        })
                    ).subscribe({
                        next: (x) => {
                            this.setCurrentReport(currentReport);
                            this.messageService.add({
                                summary: 'Reports.renameReport',
                                severity: messageSeverityEnum.success,
                                detail: 'Reports.renameReport.success'
                            });
                            // AD-15472 - REMOVE AFTER WE FIX WEBSOCKETS
                            this.updateReportListItems();
                        }, error: (x) => {
                            this.messageService.add({
                                summary: 'Reports.renameReport',
                                severity: messageSeverityEnum.error,
                                detail: 'Reports.renameReport.fail'
                            });
                        }
                    });

                    return of(null);
                }
            }
        };
        this.coreDialogService.open(TextInputDialogComponent, inputConfig);
    }

    /**
     * Update from LHP Management Dialog table
     * @param id
     * @param name
     */
    public updateReportName(id: number, name: string) {
        if (this.reportListItems.some(report => report.name === name && report.id !== id)) {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: 'Reports.renameReport.fail',
                detail: 'Reports.saveReportDuplicateNameErrorMessage'
            });
            this.updateReportListItems();
            return;
        }
        this.getReport(id).pipe(
            mergeMap(_report => {
                _report.name = name;
                return this.reportsApi.update(_report.toApiModel());
            })
        ).subscribe({
            next: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Reports.renameReport.success',
                    detail: this.localizationService.transform('Reports.saveReportSuccessMessage', {name: name})
                });
                this.updateReportListItems();
            },
            error: (err) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Reports.renameReport.fail',
                    detail: 'Reports.renameReport.fail.detail'
                });
                this.updateReportListItems();
            }
        });
    }

    public confirmCopyReport(reportToCopy: ReportListItemModel){
        const inputConfig: NewResourceDialogModel = {
            inputs: {
                heading: 'Reports.copyreport',
                initialValue: reportToCopy.name + this.localizationService.transform('Common.dialog.newResource.copy.suffix'),
                currentGroup: reportToCopy.groupId,
                okLabel: 'Common.dialog.newResource.copy',
                formValidator: (name: string) => {
                    if (this.reportListItems.find(report => report.name === name.trim())) {
                        return ['Reports.saveReportDuplicateNameErrorMessage'];
                    } else {
                        return [];
                    }
                },
                submit: (name: string, group?: DisplayGroupModel) => {
                    this.getReport(reportToCopy.id).pipe(
                        switchMap(_report => this.loadReportSavedSearches(_report)),
                        map(report => {
                            const reportModel = report.clone(true, true);
                            for (const block of reportModel.blocks) {
                                for (const series of (block.displayElement as ChartDisplayElementModel)._series) {
                                    delete series.id;
                                }
                            }
                            return reportModel;
                        }),
                        switchMap(_report => {
                            return this.saveReportSavedSearches(_report).pipe(map(x => _report));
                        }),
                        switchMap( _report => {
                            _report.name = name;
                            _report.groupId = group?.id;

                            return this.reportsApi.create(_report.toApiModel());
                        }),
                        map((id: number) => {
                            if (id) {
                                this.routingService.gotoReport(id);
                            }
                        }),
                        catchError(err => {
                            this.messageService.add({
                                summary: 'Reports.copyreport',
                                severity: messageSeverityEnum.error,
                                detail: 'Discover.confirmDialog.copy.error'});

                            throw err;
                        })
                    ).subscribe(x => {
                        this.messageService.add({
                            summary: 'Reports.copyreport',
                            severity: messageSeverityEnum.success,
                            detail: 'Discover.confirmDialog.copy.success'
                        });

                        // AD-15472 - REMOVE AFTER WE FIX WEBSOCKETS
                        this.updateReportListItems();
                    });

                    return of(null);
                }
            }
        };
        this.coreDialogService.open(NewResourceDialogComponent, inputConfig);
    }

    /**
     * Helper method that deletes a report from a ReportListItemModel
     */
    public deleteReport(reportListItem: ReportListItemModel) {
        const successMessage: ToastMessageOptions = {
            severity: messageSeverityEnum.success,
            summary: this.localizationService.transform('Reports.deleteReportSuccessHeading'),
            detail: this.localizationService.transform('Reports.deleteReportSuccessMessage', {
                name: reportListItem.name
            })
        } as ToastMessageOptions;

        const errorMessage: ToastMessageOptions = {
            severity: messageSeverityEnum.error,
            summary: this.localizationService.transform('Reports.deleteReportErrorHeading'),
            detail: this.localizationService.transform('Reports.deleteReportErrorMessage', {
                name: reportListItem.name
            })
        } as ToastMessageOptions;

        this.reportsApi.delete(reportListItem.id).subscribe({
            next: () => {
                this.messageService.add(successMessage);

                if (this.currentReport && reportListItem.id === this.currentReport.id) {
                    this.routingService.gotoReports();
                }

                // AD-15472 - REMOVE AFTER WE FIX WEBSOCKETS
                this.updateReportListItems();
            },
            error: () => this.messageService.add(errorMessage),
        });
    }

    public updateReportVisibility(id: number, hidden: boolean, button: LeftSidebarListItemButton): void {
        button.loading = true;
        this.getReport(id).pipe(
            tap(report => report.hidden = hidden),
            switchMap(report => this.reportsApi.update(report.toApiModel()))
        ).subscribe({
            next: () => this.updateReportListItems(button),
            error: () => button.loading = false,
        });
    }

    public bulkUpdateReportVisibility(ids: number[], action: ActionsEnum) {
        const hidden = action === ActionsEnum.hide;
        const observables = ids.map(id => {
            return this.reportsApi.get(id).pipe(
                switchMap(report => {
                    report.hidden = hidden;
                    return this.reportsApi.update(report);
                })
            );
        });
        forkJoin(observables).subscribe({
            next: (result) => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Reports.bulkUpdateReportSuccessHeading',
                    detail: this.localizationService.transform('Reports.bulkUpdateReportSuccessMessage', {number: result.length})
                });
                this.updateReportListItems();
            },
            error: (err) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Reports.bulkUpdateReportErrorHeading',
                    detail: 'Reports.bulkUpdateReportErrorMessage'
                });
                this.updateReportListItems();
            }
        });
    }

    /**
     * Adds provided reports to a group (or removes group ID if Uncategorized is selected)
     * @param reportIds
     * @param groupId Group ID or undefined if Uncategorized is selected
     */
    public updateReportGroup(reportIds: number[], group: DisplayGroupModel) {
        let request;
        if (group.id) {
            request = this.reportsApi.updateReportDisplayGroup(reportIds, [], group.id);
        } else {
            request = this.reportsApi.removeReportsFromDisplayGroup(reportIds);
        }
        request.subscribe({
            next: () => {
                this.updateReportListItems();
                if (reportIds.length === 1) {
                    const itemName = this.reportListItems.find(report => report.id === reportIds[0]).name;
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: this.localizationService.transform('Common.lhp.moveToGroup.success.summary'),
                        detail: this.localizationService.transform(
                            'Common.lhp.moveToGroup.success.detail.single',
                            {itemName: itemName, groupName: group.name}
                        )
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: this.localizationService.transform('Common.lhp.moveToGroup.success.summary'),
                        detail: this.localizationService.transform(
                            'Common.lhp.moveToGroup.success.detail.multiple',
                            {number: reportIds.length, name: group.name}
                        )
                    });
                }
            },
            error: (err) => {
                this.updateReportListItems();
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Common.lhp.moveToGroup.error.summary'),
                    detail: this.localizationService.transform('Common.lhp.moveToGroup.error.detail')
                });
            }
        });
    }

    /**
     * Removes group ID from all provided reports
     * @param reportIds topic IDs
     */
    public removeAllReportsFromGroup(reportIds: number[]) {
        this.reportsApi.removeReportsFromDisplayGroup(reportIds).subscribe({
            next: () => {
                this.updateReportListItems();
            }
        });
    }

    public updateReportFavorites(addIds: number[], removeIds: number[]) {
        this.reportsApi.updateReportsFavorites(addIds, removeIds).subscribe({
            next: () => {
                this.updateReportListItems();
            },
            error: (err) => {
                this.updateReportListItems();
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Common.lhp.moveToGroup.error.summary'),
                    detail: this.localizationService.transform('Common.lhp.moveToGroup.error.detail')
                });
            }
        });
    }

    /**
     * Garbage collector for subscriptions
     */
    public ngOnDestroy(): void {
        this.subscriptions.forEach(
            subscription => {
                subscription.unsubscribe();
            }
        );
    }

    /**
     * Saves the current report to the API
     */
    public saveCurrentReport(): Observable<null> {
        if (this.isSaving || !this.currentReport) {
            return of(null);
        }

        if (!this.validateReportName(this.currentReport?.name)){
            return of(null);
        }


        this.isSaving = true;

        const successMessage: ToastMessageOptions = {
            severity: messageSeverityEnum.success,
            summary: this.localizationService.transform('Reports.saveReportSuccessHeading'),
            detail: this.localizationService.transform('Reports.saveReportSuccessMessage', {
                name: this.currentReport.name })
        } as ToastMessageOptions;

        const defaultErrorMessage: ToastMessageOptions = {
            severity: messageSeverityEnum.error,
            summary: this.localizationService.transform('Reports.saveReportErrorHeading'),
            detail: this.localizationService.transform('Reports.saveReportErrorMessage', {
                name: this.currentReport.name })
        } as ToastMessageOptions;

        // save all of the curated saved searches first
        return this.saveReportSavedSearches(this.currentReport).pipe(
            mergeMap(() => {
                this.cleanCurrencyFromBlock();
                return this.reportsApi.save(this.currentReport.toApiModel());
            }),
            mergeMap((response) => {
                if (this.currentReport.id) {
                    // Update case
                    this.setCurrentReportFromApi(this.currentReport.id);
                } else {
                    // Create case
                    this.currentReport.id = response;

                    // Copy the current report to the cached report because they should now be in sync
                    this.cachedCurrentReport = this.currentReport.clone();
                    this.canLeaveBypass = true;

                    this.routingService.gotoReport(response);
                }

                this.messageService.add(successMessage);
                this.isSaving = false;

                // AD-15472 - REMOVE AFTER WE FIX WEBSOCKETS
                this.updateReportListItems();
                return of(null);
            }),
            catchError((error) => {
                this.updateReportListItems();

                // need to check if string because startsWith is a string function & error.error would
                // return as an object sometimes and it would silently fail
                if (error && error.error && isString(error.error) &&
                    error.error.startsWith(ReportsService.DUPLICATE_ERROR_STRING)
                ) {
                    // Duplicate name error case
                    const duplicateNameErrorMessage: ToastMessageOptions = {
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('Reports.saveReportErrorHeading'),
                        detail: this.localizationService.transform('Reports.saveReportDuplicateNameErrorMessage')
                    } as ToastMessageOptions;

                    this.messageService.add(duplicateNameErrorMessage);
                } else {

                    this.messageService.add(defaultErrorMessage);
                }

                this.isSaving = false;
                throw error;
            })
        );
    }

    /**
     *  check the report name, if the name is less than 3 characters, return false
     */

    public validateReportName(name: string): boolean{
        if (name?.length < 3){
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('Reports.saveReportErrorHeading'),
                detail: this.localizationService.transform('Reports.save.nameLenghError')
            });
            return false;
        }
        return true;
    }

    /**
     *  check whether report name is duplicated
     */

    public isDuplicateName(name: string): boolean{
        if (this.reportListItems.find(x => x.name === name.trim())){
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('Reports.saveReportErrorHeading'),
                detail: this.localizationService.transform('Reports.saveReportDuplicateNameErrorMessage')
            });
            return false;
        }
        return true;
    }

    /**
     * Create new report
     */
    public createNewReport(newReport: ReportModel): Observable<number> {
        if (!newReport) {
            return of(null);
        }

        if (!this.validateReportName(newReport?.name)){
            return of(null);
        }

        this.isSaving = true;

        // save all of the curated saved searches first
        return this.saveReportSavedSearches(newReport).pipe(
            mergeMap(() => {
                delete newReport.globalSourceSingleSeries?.styles?.curency;
                newReport.blocks.forEach( _block => {
                    const chartDisplayElement = _block.displayElement as ChartDisplayElementModel;
                    chartDisplayElement.series.forEach( _series => delete _series?.styles?.currency);
                });
                return this.reportsApi.save(newReport.toApiModel());
            }),
            map((response) => {
                newReport.id = response;

                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Reports.saveReportSuccessHeading'),
                    detail: this.localizationService.transform('Reports.saveReportSuccessMessage', {
                        name: newReport.name })
                });
                this.isSaving = false;
                this.canLeaveBypass = true;

                // AD-15472 - REMOVE AFTER WE FIX WEBSOCKETS
                this.updateReportListItems();
                return newReport.id;
            }),
            catchError((error) => {
                this.updateReportListItems();

                // need to check if string because startsWith is a string function & error.error would
                // return as an object sometimes and it would silently fail
                if (error?.error &&
                    isString(error.error) &&
                    error.error.startsWith(ReportsService.DUPLICATE_ERROR_STRING)
                ) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('Reports.saveReportErrorHeading'),
                        detail: this.localizationService.transform('Reports.saveReportDuplicateNameErrorMessage')
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('Reports.saveReportErrorHeading'),
                        detail: this.localizationService.transform('Reports.saveReportErrorMessage', {
                            name: newReport.name })
                    });
                }

                this.isSaving = false;
                throw error;
            })
        );
    }

    /**
     * Getter for showQuickReportProvider
     *
     * @returns Observable
     */
    public getShowQuickReportProvider(): Observable<boolean> {
        return this.showQuickReportDialogProvider;
    }

    /**
     * Saves the filter search objects from the report charts
     */
    private saveReportSavedSearches(report: ReportModel): Observable<any[]> {
        const blocks = report.blocks;
        const requests: Observable<any>[] = [];
        const seriesArray: ReportSeriesModel[] = [];

        for (const block of blocks) {
            (block.displayElement as ChartDisplayElementModel).series.map(series => {
                seriesArray.push(series);
            });
        }

        if (report.globalSourceSingleSeries) {
            seriesArray.push(report.globalSourceSingleSeries);
        }

        if (report.globalSourceMultiSeries?.length > 0) {
            report.globalSourceMultiSeries.map(seriesItem => {
                seriesArray.push(seriesItem);
            });
        }

        for (const series of seriesArray) {
            if (series.mentions.searchCriteriaId) {
                if (series.mentions.filters) {
                    // already exists, update
                    series.mentions.filters.id = series.mentions.searchCriteriaId;
                    series.mentions.filters.searchCriteria.query.dateQuery = null;
                    requests.push(
                        this.savedSearchesApi.update(series.mentions.filters.toApiModel())
                    );
                } else {
                    // filters remove, so delete
                    requests.push(
                        this.savedSearchesApi.deleteById(series.mentions.searchCriteriaId).pipe(
                            map(() => {
                                delete series.mentions.searchCriteriaId;
                            }))
                    );
                }
            } else if (series.mentions.filters) {
                // create new
                if (!series.mentions.filters.name) {
                    // Name can't be blank, must be unique
                    series.mentions.filters.name = `${+new Date()}${Math.random()}`;
                }
                series.mentions.filters.type = SavedSearchType.REPORTS;
                series.mentions.filters.searchCriteria.query.dateQuery = null;

                requests.push(
                    this.savedSearchesApi.create(series.mentions.filters.toApiModel()).pipe(
                        map((id) => {
                            series.mentions.searchCriteriaId = id;
                            return id;
                        }))
                );
            }
        }

        if (requests.length < 1) {
            return of([]);
        }
        return forkJoin(requests);
    }

    /**
     * currently has three functionalities
     * 1) set displayElement colorMap depending on series
     * 2) sets series metric depending on displayElement
     * 3) sets the currency of the block to its series
     */
    private updateDisplayElementFromSeries(block: BlockModel): void {
        const displayElement = <ChartDisplayElementModel>block.displayElement;

        let hasAVEMetric = false;
        if (!!displayElement.metrics){
            if (displayElement.metrics.indexOf(ChartMetricsEnum.ave) !== -1){
                hasAVEMetric = true;
            }
        } else if (displayElement.metric === ChartMetricsEnum.ave){
            hasAVEMetric = true;
        }

        if (hasAVEMetric && !displayElement.styles.currency){
            // For the situations that user changes the report currency before adding any charts
            // we need to read the currency from the reportGlobalSourceSeries and apply it on the displayElement
            const currencyOnReport = this.currentReport.globalSourceSingleSeries?.styles?.currency ||
                this.currentReport.globalSourceMultiSeries?.[0]?.styles?.currency;
            if (currencyOnReport) {
                displayElement.styles.currency = currencyOnReport;
            } else {
                // use user's preference currency if there's no currency on report
                displayElement.styles.currency = this.currency;
            }
        }

        const series = displayElement.series;
        series?.forEach((series_, i) => {
            series_.metric = displayElement.metric;
            series_.metrics = displayElement.metrics;
            series_.hasPreviousPeriod = displayElement.styles.chartDisplaySettings.previousPeriod;
            if (hasAVEMetric){
                series_.styles.currency = displayElement.styles.currency;
                series_.styles.currencySymbol = currencySymbolMap.get(displayElement.styles.currency);
            } else {
                delete series_.styles.currency;
                delete series_.styles.currencySymbol;
            }

            if (series_.color) {
                switch (block.type.key) {
                    case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
                    case ReportBlockTypeEnum.TOP_10_OUTLETS:
                    case ReportBlockTypeEnum.TOP_HASHTAGS:
                        // backwards compat layer - the color for the series used to be stored on the key 'barColor',
                        // but now is stored properly on the series. this conditional can be removed after some time
                        // has passed, as just interacting with a report will cause this code to run, which migrates
                        // the color property.
                        if (displayElement.styles.colorMap['barColor']) {
                            series_.color = displayElement.styles.colorMap['barColor'];
                            delete displayElement.styles.colorMap['barColor'];
                        }
                        displayElement.styles.colorMap[`seriesColor${i}`] = series_.color;
                        break;

                    case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                    case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
                    case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                    case ReportBlockTypeEnum.SHARE_OF_VOICE:
                    case ReportBlockTypeEnum.COVERAGE_COMPARISON:
                    case ReportBlockTypeEnum.REFERRAL_TREND:
                    case ReportBlockTypeEnum.RECENT_TOPICS:
                        displayElement.styles.colorMap[`seriesColor${i}`] = series_.color;
                        break;
                    case ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK:
                        // backwards compatibility layer - this chart was single bar-color chart so the color was stored on the key 'barColor',
                        // since the color customization has been added to each bar, update color map and remove barColor key
                        if (displayElement.styles.colorMap['barColor']) {
                            displayElement.networkSelection?.forEach(networkType => {
                                displayElement.styles.colorMap[networkType] = displayElement.styles.colorMap['barColor'];
                            });
                            delete displayElement.styles.colorMap['barColor'];
                        }
                        delete displayElement.styles.colorMap[`seriesColor${i}`];
                        break;
                    default:
                        break;
                }
            }
        });
    }

    /**
     * remove currency from block before we send the report to B/E, currency should only exists on global series.
     */
    private cleanCurrencyFromBlock(){
        if (!this.currentReport){
            return;
        }

        this.currentReport.blocks.forEach( _block => {
            delete _block.displayElement.styles.currency;
            const chartModel = _block.displayElement as ChartDisplayElementModel;
            chartModel.series.forEach( _series => delete _series.styles.currency);
        });

    }

    private updateCurrentReportBlocks(): void {
        this.currentReport.blocks.forEach(block => {
            this.updateDisplayElementFromSeries(block);
        });
    }

    /**
     * Gets the list of reports, a simplified model for displaying in lists/dropdowns
     */
    public getReportListItems(): Observable<ReportListItemModel[]> {
        if (!this.reportListItems) {
            this.updateReportListItems();
        }

        return this.reportListItemsProvider;
    }

    public getFilterListItems(): Observable<AuditableModel[]> {
        return this.getReportListItems();
    }

    /**
     * Fetches the report list from the API and pushes it to provider
     */
    public updateReportListItems(visibilityButton?: LeftSidebarListItemButton): void {
        this.reportsApi.getList().subscribe(
            listItems => {
                this.reportListItems = listItems.map<ReportListItemModel>(listItem => new ReportListItemModel(listItem));

                if (this.reportListItems){
                    this.reportListItemsProvider.next(this.reportListItems);
                }

                if (visibilityButton) {
                    visibilityButton.loading = false;
                }
            }
        );
    }

    public navigateToCloneOfExistingReport(cloneId: number) {
        this.canLeaveBypass = true;
        this.routingService.gotoReport(undefined, undefined, cloneId);
    }

    /**
     * Fetches a report by ID from the back end, clones it and sets up the providers as if it were a new report
     */
    public cloneFromExistingReport(id: number): void {
        this.clearCurrentRequest();
        this.currentRequest = this.getReport(id).pipe(
            tap(report => {
                this.currentReport = report.clone(true);
                for (const block of this.currentReport.blocks) {
                    for (const series of (block.displayElement as ChartDisplayElementModel)._series) {
                        delete series.id;
                    }
                }
            }),
            switchMap(_ => this.loadReportSavedSearches(this.currentReport))
        ).subscribe(_ => {
            this.currentReport.getSeries().forEach(series => {
                delete series.mentions.searchCriteriaId;

                if (series.mentions.filters) {
                    series.mentions.filters.anonymize();
                }
            });
            // Set it to null so it always appears dirty until saved
            this.cachedCurrentReport = null;
            this.currentReportProvider.next(this.currentReport);

            // AD-15472 - REMOVE AFTER WE FIX WEBSOCKETS
            this.updateReportListItems();
        });
    }

    /**
     * Get a report by ID
     * @param id the report id
     */
    public getReport(id: number): Observable<ReportModel> {
        const monitoringCalls = this.currentUserStateService.getPermissions().pipe(
            switchMap((permissions) => {
                const hasAdvanceMonitoring = permissions.includes(userPermissionsEnum.advancedMonitoring);

                const observables: Observable<any>[] = [
                    hasAdvanceMonitoring ? this.folderService.all$.pipe(filter(x => !!x)) : of([]),
                    hasAdvanceMonitoring ? this.tagsService.all$.pipe(filter(x => !!x)) : of([]),
                    hasAdvanceMonitoring ? this.tagGroupsService.getTagGroupsList().pipe(filter(x => !!x)) : of([]),
                    hasAdvanceMonitoring ? this.topicsService.getTopics().pipe(filter(x => !!x)) : of([]),
                    hasAdvanceMonitoring ? this.sourceGroupService.all$.pipe(filter(x => !!x)) : of([])
                ];

                if (permissions.includes(userPermissionsEnum.mediaSubscriptions) || permissions.includes(userPermissionsEnum.socialIntelligence)) {
                    observables.push(
                        this.mediaSubscriptionService.all$.pipe(filter(x => !!x))
                    );
                    if (permissions.includes(userPermissionsEnum.socialIntelligence)) {
                        observables.push(
                            this.themeService.all$.pipe(filter(x => !!x))
                        );
                    } else {
                        observables.push(
                            of([])
                        );
                    }
                } else {
                    observables.push(
                        of([])
                    );
                }

                return combineLatest(observables);
            }),
        );

        const res = combineLatest([
            this.reportsApi.get(id),
            monitoringCalls,
        ]).pipe(
            map(([reportApi, monitoringItems]) => {
                const ids = monitoringItems.map(items => (items as {id: string | number}[]).map(item => +item.id));

                this.processReportSeriesBadData(reportApi, ids);

                const newReport = new ReportModel(reportApi);
                newReport.blocks.forEach( _block => {
                    let currency = newReport.globalSourceSingleSeries?.styles.currency || newReport.globalSourceMultiSeries?.[0]?.styles.currency;
                    if (!currency){
                        currency = this.currency;
                    }
                    _block.displayElement.styles.currency = currency;
                    this.updateDisplayElementFromSeries(_block);
                });
                return newReport;
            }),
            catchError((error) => {
                if(error.status === 404){
                    this.routingService.gotoReports({
                        queryParams: { notFound: 'true' }
                    });
                    return EMPTY;
                }
                throw error;
            }),
            take(1)
        );

        return res;
    };

    private processReportSeriesBadData(reportApi: ReportApiModel, ids: number[][]): void {
        const data = JSON.parse(reportApi.data);
        this.processSeriesBadData(data?.globalSourceSingleSeries, ids);
        if (data?.globalSourceMultiSeries) {
            data.globalSourceMultiSeries.forEach(series => this.processSeriesBadData(series, ids));
        }
        data?.blocks.forEach(block => {
            block.series.forEach(series => this.processSeriesBadData(series, ids));
        });
        reportApi.data = JSON.stringify(data);
    }

    // b.e returned series may contain folders/topics/tags/tagGroups/sourceGroups/subscriptions that have been deleted
    // here we reset those invalid series
    private processSeriesBadData(series: ReportSeriesApiModel, ids: number[][]): void {
        if (!series || ids.length === 0) {
            return;
        }
        const [
            folderIds,
            tagIds,
            tagGroupIds,
            topicIds,
            sourceGroupIds,
            subscriptionIds,
            themeIds
        ] = ids;

        const seriesSourceTypesToCollectionIdsMap: Map<SeriesSourceTypesEnum, number[]> = new Map<SeriesSourceTypesEnum, number[]>([
            [ SeriesSourceTypesEnum.topic, topicIds ],
            [ SeriesSourceTypesEnum.folder, folderIds ],
            [ SeriesSourceTypesEnum.tag, tagIds ],
            [ SeriesSourceTypesEnum.tagGroup, tagGroupIds ],
            [ SeriesSourceTypesEnum.sourceGroup, sourceGroupIds ],
            [ SeriesSourceTypesEnum.subscription, subscriptionIds ],
            [ SeriesSourceTypesEnum.theme, themeIds ]
        ]);

        // if the series contains bad data (e.g. deleted folder)
        // reset folderId = 0 to indicate it is emplty
        // we reset id to null, so that b/e will save it as a new series once we save the report
        if (series.sourceId > 0 && seriesSourceTypesToCollectionIdsMap.get(series.sourceType)
            && !seriesSourceTypesToCollectionIdsMap.get(series.sourceType).includes(series.sourceId)) {
            series.id = 0;
            series.sourceId = 0;
            series.label = '';
        }
    }

    private clearCurrentRequest(): void {
        if (this.currentRequest) {
            this.currentRequest.unsubscribe();
            this.currentRequest = null;
        }
    }

    /**
     * Based on route changes, determines what the currentReport should be and pushes it to the provider
     */
    private updateReportFromRoute(route: ReportsRouteModel): void {
        switch (route.type) {
            case ReportsRouteTypeEnum.report:
                if (route.reportsRouteValueMap) {
                    // Viewing a specific report
                    this.setCurrentReportFromApi(+route.reportsRouteValueMap.reportId);
                } else {
                    this.createNewReportFromRoute(route);
                }
                break;

            case ReportsRouteTypeEnum.reportsHype:
            default:
                // Hype page
                this.currentReport = null;
                break;
        }
    }

    /**
     * Creates a new report based on route
     */
    private createNewReportFromRoute(route: ReportsRouteModel): void {
        if (!route || !route.queryParamMap) {
            this.setNewReport();

            return;
        }

        const quickReport: boolean = !!route.queryParamMap.get('quickReport');
        const queryParamCloneId = route.queryParamMap.get('cloneId');
        const cloneId: number = queryParamCloneId ? +queryParamCloneId : null;

        if (cloneId) {
            this.makeReportClean();
            this.cloneFromExistingReport(+cloneId);

            return;
        }

        if (!quickReport) {
            this.setNewReport();

            return;
        }

        this.makeReportClean();
    }

    /**
     * Sets the current report to a default report, this is required for the case of pressing new when you're on new
     */
    public setNewReport(): void {
        this.currentReport = null;
        this.currentReportProvider.next(this.currentReport);

        this.currentReport = this.getDefaultReport();
        this.cachedCurrentReport = this.currentReport.clone(false);
        this.currentReportProvider.next(this.currentReport);
    }

    /**
     * Fetches the report from the api and loads all necessary resources
     */
    public setCurrentReportFromApi(id: number): void {
        this.setCurrentReport(null);
        this.clearCurrentRequest();
        this.currentRequest = this.getReport(id).pipe(
            mergeMap(report => {
                return this.loadReportSavedSearches(report);
            })
        ).subscribe({
            next: (report) => {
                this.currentReport = report;
                this.cachedCurrentReport = this.currentReport.clone(false);
                this.currentReportProvider.next(this.currentReport);
            },
            error: () => {
                // @TODO handle 404's and redirect to hype page when coming from a route
                // other modules do this by returning a status code to the route handler
            }
        });
    }

    /**
     * Loads the saved filter searches for the report charts
     */
    loadReportSavedSearches(report: ReportModel): Observable<ReportModel> {
        const series = report.getSeries();
        if (report.globalSourceSingleSeries?.mentions?.searchCriteriaId) {
            series.push(report.globalSourceSingleSeries);
        }

        if (report.globalSourceMultiSeries?.length > 0) {
            report.globalSourceMultiSeries.forEach(seriesItem => {
                if (seriesItem.mentions.searchCriteriaId) {
                    series.push(seriesItem);
                }
            });
        }

        this.loadMentions(report);

        let searchIds = series.filter( item => !!item.mentions.searchCriteriaId).map( item => item.mentions.searchCriteriaId);
        searchIds = [...new Set(searchIds)];

        if (searchIds.length < 1) {
            return of(report);
        }

        return this.savedSearchesApi.getSavedSearchBulk(searchIds).pipe(mergeMap(search => {
            const loadRegions: Observable<void>[] = [];
            series.forEach(item => {
                if (item.mentions.searchCriteriaId) {
                    item.mentions.filters = new MentionSearchModel(search[item.mentions.searchCriteriaId]);
                    loadRegions.push(this.loadSavedSearchRegions(item.mentions.filters));
                }
            });

            if (loadRegions.length === 0){
                return of(report);
            }

            return forkJoin(loadRegions).pipe(
                map(topics => {
                    return report;
                })
            );
        }));
    }

    /**
     * for topCuratedMentions, it will do a batch get of the saved mentions, and update with the latest mentions
     */
    loadMentions(report: ReportModel): void {
        const blocks = report?.blocks;
        if (blocks?.length < 1) {
            return;
        }

        blocks.forEach(block => {
            if ((block.displayElement as ChartDisplayElementModel)?.styles?.topCuratedMentions?.length) {
                const chartDisplayElement = block.displayElement as ChartDisplayElementModel;
                const mentionIds = chartDisplayElement?.styles?.topCuratedMentions.map(mention => mention.id);
                this.mentionsApi.getMentionsBatched(mentionIds).subscribe(mentions => {
                    for (const index in chartDisplayElement.styles.topCuratedMentions) {
                        if (mentions[chartDisplayElement.styles.topCuratedMentions[index].id]) {
                            chartDisplayElement.styles.topCuratedMentions[index] = mentions[chartDisplayElement.styles.topCuratedMentions[index].id];
                        }
                    }

                    chartDisplayElement.topCuratedMentions = JSON.parse(
                        JSON.stringify(chartDisplayElement?.styles?.topCuratedMentions)
                    )?.map(x => new Mention(x)) || [];

                    chartDisplayElement.series[0].mentionProvider.next(chartDisplayElement.topCuratedMentions);
                });
            }
        });
    }

    /**
     * Loads the regions for a search object
     */
    loadSavedSearchRegions(search: MentionSearchModel): Observable<void> {
        const query = search.searchCriteria.query;
        const includeRegions = query.rawIncludedRegions;
        if (!MentionSearchQueryModel.isRawRegionEmpty(includeRegions)) {
            return this.journodexService.mapToComplexRegionInfo(includeRegions).pipe(map(
                regions => {
                    query.includedRegions = regions;
                    query.excludedRegions = [];
                    query.rawIncludedRegions = null;
                    query.rawExcludedRegions = null;
                }
            ));
        }
        return of(null);
    }

    /**
     * Re-fetches the current report from the API
     */
    public refreshCurrentReport(): void {
        this.setCurrentReportFromApi(this.currentReport.id);
    }

    /**
     * Gets the default report
     */
    private getDefaultReport() {
        const newReport: ReportModel = new ReportModel();
        return newReport;
    }

    /**
     * Filters a set of series for a chart to only the ones that are valid based on the block model definition
     */
    private filterValidSeriesForChart(series: ReportSeriesModel[], chartBlock: BlockTypeModel): ReportSeriesModel[] {
        const seriesField: SeriesPickerFieldModel = chartBlock.fields.find(field => field instanceof SeriesPickerFieldModel) as any;
        const seriesCompareField: SeriesPickerCompareFieldModel = chartBlock.fields.find(field => field instanceof SeriesPickerCompareFieldModel) as any;

        let availableTypes: SeriesTypeEnum[] = [];

        if (seriesField) {
            availableTypes = seriesField.typeOptions;
        } else if (seriesCompareField) {
            availableTypes = seriesCompareField.typeOptions;
        } else {
            // Should not happen so long as the blocks are actually chart blocks, but we may have non-chart blocks in the future, so guard it anyway
            return [];
        }

        return series.filter(seriesItem => availableTypes.includes(seriesItem.type));
    }

    /**
     * Creates a quick report
     * (a report with standard charts based on provided data)
     */
    public createQuickReport(
        series: ReportSeriesModel[][],
        reportTitle: string,
        globalSingleSeries?: ReportSeriesModel[],
        globalMultiSeries?: ReportSeriesModel[]
    ): void {
        const quickReport: ReportModel = new ReportModel();
        quickReport.title = reportTitle;

        let blockType: BlockTypeModel;
        let block: BlockModel;
        for (const [index, chartSeries] of series.entries()) {
            const firstSeries = chartSeries[0];
            blockType = reportBlockTypesMap.get(firstSeries.chartType);
            const validSeries = this.filterValidSeriesForChart(chartSeries, blockType);
            // Don't add the chart if it has not valid series
            if (validSeries.length > 0) {
                block = this.getChartBlockModel(blockType, validSeries);
                block.id = index + 1;
                // sets the x and y positions for default charts
                switch (blockType.key) {
                    case ReportBlockTypeEnum.COVERAGE_SUMMARY:
                        block.x = 0;
                        block.y = 0;
                        break;
                    case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                        block.x = 2;
                        block.y = 0;
                        break;
                    case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN:
                        block.x = 0;
                        block.y = 3;
                        break;
                    case ReportBlockTypeEnum.TOP_10_OUTLETS:
                        block.x = 0;
                        block.y = 9;
                        break;
                    case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
                        block.x = 2;
                        block.y = 9;
                        break;
                    case ReportBlockTypeEnum.TOP_HASHTAGS:
                        block.x = 4;
                        block.y = 9;
                        break;
                    case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN:
                        block.x = 0;
                        block.y = 6;
                        break;
                    case ReportBlockTypeEnum.SENTIMENT_TREND:
                        block.x = 3;
                        block.y = 4;
                        break;
                    case ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME:
                        block.x = 4;
                        block.y = 3;
                        break;
                    default:
                        break;
                }
                quickReport.blocks.push(block);
            }
        }

        this.currentReport = quickReport;
        if (globalSingleSeries?.length > 0) {
            this.currentReport.globalSourceSingleSeries = globalSingleSeries[0];
        }
        if (globalMultiSeries?.length > 0) {
            this.currentReport.globalSourceMultiSeries = globalMultiSeries;
        }
        this.cachedCurrentReport = this.currentReport.clone(false);
        this.currentReportProvider.next(this.currentReport);
        this.canLeaveBypass = true;
    }

    /**
     * Gets a blockModel for a chart of the given blocktype and based on the given data
     *
     * @param blockType - the type of block (chart block) to create the block model of
     *
     * @param series - the data to create the chart on'
     *
     * @returns BlockModel - a chart block model
     */
    getChartBlockModel(blockType: BlockTypeModel, series: ReportSeriesModel[]): BlockModel {
        const displayElement = new blockType.displayElementType() as ChartDisplayElementModel;
        const blockKey = blockType.key as ReportBlockTypeEnum;

        displayElement.series = series.map((seriesItem) => {
            return seriesItem.clone(blockKey);
        });
        displayElement.chartSpecificType = reportBlockTypeEnumToDefaultChartSpecificType.get(blockKey);

        const { dimensions, dynamicTitle } = reportChartInfoMap.get(blockKey);
        displayElement.size = dimensions;

        switch (blockKey) {
            case ReportBlockTypeEnum.TOP_10_OUTLETS:
            case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
            case ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS:
            case ReportBlockTypeEnum.TOP_HASHTAGS:
                // overwrite series color to use the default bar color from user prefs
                displayElement.series.forEach(_series => _series.color = this.defaultBarColor);
                break;
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                displayElement.styles.colorMap = {
                    analysisColor1: this.defaultLineColor
                };
                displayElement.timeInterval = ChartTimeIntervalsEnum.DYNAMIC;
                break;
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
                displayElement.timeInterval = ChartTimeIntervalsEnum.DYNAMIC;
                break;
            case ReportBlockTypeEnum.COVERAGE_SUMMARY:
                displayElement.metrics = [
                    ChartMetricsEnum.volumeOfMentions,
                    ChartMetricsEnum.audienceReach,
                    ChartMetricsEnum.ave,
                ];
                displayElement.chartHeaderSettings.previousPeriod = true;
                break;
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN:
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON:
            case ReportBlockTypeEnum.SENTIMENT_TREND:
                displayElement.sentiments = [
                    SearchCriteriaQuerySentimentsEnum.positive,
                    SearchCriteriaQuerySentimentsEnum.negative,
                    SearchCriteriaQuerySentimentsEnum.neutral,
                    SearchCriteriaQuerySentimentsEnum.notAvailable
                ];
                const sentimentColors = this.defaultChartColors?.sentimentColors;
                displayElement.styles.colorMap = {
                    [SearchCriteriaQuerySentimentsEnum.positive]: sentimentColors?.positive,
                    [SearchCriteriaQuerySentimentsEnum.negative]: sentimentColors?.negative,
                    [SearchCriteriaQuerySentimentsEnum.neutral]: sentimentColors?.neutral,
                    [SearchCriteriaQuerySentimentsEnum.notAvailable]: sentimentColors?.notAvailable
                };
                break;
            case ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME:
                displayElement.mapChartColorScheme = MapChartColorSchemeEnum.CHOROPLETH;
                displayElement.mapChartChoroplethInterpolationType = MapChartChoroplethInterpolationTypeEnum.LINEAR;
                break;
        }

        if (dynamicTitle) {
            const titleMetric = this.determineChartMetrics(displayElement, blockKey);
            let structure = this.localizationService.transform(`Reports.chart.title.${blockKey}.structure`);
            if (blockKey === ReportBlockTypeEnum.COVERAGE_OVER_TIME && displayElement.metrics.length > 1) {
                structure = this.localizationService.transform(`Reports.chart.title.${blockKey}.structure.multiMetrics`);
            }
            displayElement.title = this.createDynamicChartTitle(
                structure,
                displayElement
            );
        } else {
            displayElement.title = this.localizationService.transform('Reports.chart.title.' + blockKey);
        }

        const block = new BlockModel(
            null,
            blockType,
            displayElement
        );
        block.rows = dimensions.height;
        block.cols = dimensions.width;

        return block;
    }

    /**
     * Determines if there are changes made that aren't saved
     */
    public canLeaveCurrentReport(): boolean {
        if (
            this.currentRoute.type === ReportsRouteTypeEnum.reportsHype
            || this.currentRoute.type === ReportsRouteTypeEnum.reportSharedReports
        ) {
            this.canLeaveBypass = false;
            return true;
        }

        if (this.canLeaveBypass) {
            this.canLeaveBypass = false;
            return true;
        }

        // when you toggle between reports quick, the currentReport can be null, and ends up showing the unsaved changes dialog
        if (!this.currentReport && this.cachedCurrentReport?.id) {
            return true;
        }
        return this.currentReport?.equals(this.cachedCurrentReport);
    }

    /**
     * This function is ONLY for the new report dialog, and should not be used in any other context
     *
     * This sets the cached copy to be the same as the current copy so that the authguard won't trigger a dialog
     */
    public makeReportClean(): void {
        if (this.currentReport) {
            this.cachedCurrentReport = this.currentReport.clone(false);
        } else {
            this.cachedCurrentReport = null;
        }
    }

    /**
     * Used by the de-activate guard and new briefing modal to determine if it should show the dialog
     */
    public showCanLeaveDialog(): Observable<boolean> {
        if (this.canLeaveCurrentReport()) {
            return of(true);
        }

        return new Observable(
            (observer: Observer<boolean>) => {
                const io: ConfirmDialogModel = {
                    inputs: {
                        heading: 'DeactivateGuard.heading',
                        messageHtml: 'DeactivateGuard.message',
                        okLabel: 'continue',
                        id: 'ReportsContinue'
                    },
                    outputs: {
                        dialogClose: (confirmed: boolean) => {
                            observer.next(confirmed);
                            observer.complete();
                        }
                    }
                };
                this.coreDialogService.open(ConfirmDialogComponent, io);
            }
        );
    }

    private determineChartMetrics(displayElement: ChartDisplayElementModel, blockKey: ReportBlockTypeEnum): ChartMetricsEnum {
        const singleMetricCharts = [
            ReportBlockTypeEnum.TOP_10_OUTLETS,
            ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME,
            ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON,
            ReportBlockTypeEnum.COVERAGE_COMPARISON,
            ReportBlockTypeEnum.SHARE_OF_VOICE,
            ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK,
            ReportBlockTypeEnum.TOP_POSTS,
            ReportBlockTypeEnum.TOP_MENTIONS,
            ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME
        ];
        const multiMetricCharts = [
            ReportBlockTypeEnum.COVERAGE_OVER_TIME,
            ReportBlockTypeEnum.COVERAGE_SUMMARY,
        ];

        if (singleMetricCharts.includes(blockKey)) {
            // Default to volumeOfMentions for quick reports
            displayElement.metric = ChartMetricsEnum.volumeOfMentions;

            if (blockKey === ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS) {
                displayElement.metric = ChartMetricsEnum.sessions;
            }

            return displayElement.metric;
        }

        if (multiMetricCharts.includes(blockKey)) {
            displayElement.metrics = [ChartMetricsEnum.volumeOfMentions];
            if (blockKey === ReportBlockTypeEnum.COVERAGE_OVER_TIME) {
                displayElement.metrics = [
                    ChartMetricsEnum.volumeOfMentions,
                    ChartMetricsEnum.ave,
                    ChartMetricsEnum.IMPRESSIONS,
                ];
                const otherColors = this.defaultChartColors?.otherColors;
                displayElement.styles.colorMap = {
                    [ChartMetricsEnum.volumeOfMentions]: otherColors?.volume,
                    [ChartMetricsEnum.IMPRESSIONS]: otherColors?.impressions,
                    [ChartMetricsEnum.ave]: otherColors?.engagements
                };
            }
            return displayElement.metrics[0];
        }
    }

    createDynamicChartTitle(structure: string, displayElement: any): string {
        let propertyNotFound = false;
        const title = structure.replace(/\[\[([A-Za-z\.]+)\]\]/g, (match, target) => {
            const properties = target.split('.');
            const property = properties[0];
            if (displayElement[property]) {
                const propertyValue = typeof displayElement[property] === 'string' ?
                    displayElement[property] : displayElement[property][0];
                return this.localizationService.transform('Reports.chart.title.' + target.replace(property, propertyValue));
            } else {
                propertyNotFound = true;
            }
        }).trim();

        return propertyNotFound ? '' : title;
    }
}
